import { WSEvent } from "@shared/constants";
import type {
  CreateShareInput,
  CreateShareListInput,
  Share,
  ShareStats,
  ShareWithResult,
  UpdateShareInput,
} from "@shared/types";
import { defineStore } from "pinia";
import type { Socket } from "socket.io-client";
import { ref } from "vue";
import { api } from "../utils/api";

/**
 * Shares Store - Manage share links with WebSocket sync
 *
 * Note: Shares can contain multiple vehicles (N:M relationship)
 */
export const useSharesStore = defineStore("shares", () => {
  // State
  const shares = ref<ShareWithResult[]>([]);
  const stats = ref<ShareStats>({
    total: 0,
    active: 0,
    expired: 0,
    total_views: 0,
  });
  const isLoading = ref(false);

  // Actions
  const fetchShares = async (): Promise<void> => {
    try {
      isLoading.value = true;
      const data = await api.get<ShareWithResult[]>("/api/shares");
      shares.value = data;
    } catch (error) {
      console.error("Failed to fetch shares:", error);
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchStats = async (): Promise<void> => {
    try {
      const data = await api.get<ShareStats>("/api/shares/stats");
      stats.value = data;
    } catch (error) {
      console.error("Failed to fetch share stats:", error);
      throw error;
    }
  };

  const createShare = async (input: CreateShareInput): Promise<Share> => {
    try {
      isLoading.value = true;
      const share = await api.post<Share>("/api/shares", input);

      await fetchShares();
      await fetchStats();

      return share;
    } catch (error) {
      console.error("Failed to create share:", error);
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  const createShareList = async (input: CreateShareListInput): Promise<Share> => {
    try {
      isLoading.value = true;
      const share = await api.post<Share>("/api/shares/lists", input);

      await fetchShares();
      await fetchStats();

      return share;
    } catch (error) {
      console.error("Failed to create share list:", error);
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  const updateShare = async (
    id: string,
    input: UpdateShareInput,
  ): Promise<Share> => {
    try {
      isLoading.value = true;
      const share = await api.put<Share>(`/api/shares/${id}`, input);

      const index = shares.value.findIndex((s) => s.id === id);
      if (index !== -1) {
        await fetchShares(); // Refetch to get updated vehicles list
      }

      return share;
    } catch (error) {
      console.error("Failed to update share:", error);
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  const deleteShare = async (id: string): Promise<void> => {
    try {
      isLoading.value = true;
      await api.delete(`/api/shares/${id}`);

      shares.value = shares.value.filter((s) => s.id !== id);
      await fetchStats();
    } catch (error) {
      console.error("Failed to delete share:", error);
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  const getShareUrl = (uuid: string): string => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/share/${uuid}`;
  };

  const copyShareUrl = async (uuid: string): Promise<void> => {
    const url = getShareUrl(uuid);
    await navigator.clipboard.writeText(url);
  };

  // WebSocket Listeners
  const setupWebSocketListeners = (socket: Socket): void => {
    socket.on(WSEvent.SHARE_CREATED, async () => {
      await fetchShares();
      await fetchStats();
    });

    socket.on(WSEvent.SHARE_UPDATED, async () => {
      await fetchShares();
      await fetchStats();
    });

    socket.on(WSEvent.SHARE_DELETED, async () => {
      await fetchShares();
      await fetchStats();
    });
  };

  return {
    // State
    shares,
    stats,
    isLoading,

    // Actions
    fetchShares,
    fetchStats,
    createShare,
    createShareList,
    updateShare,
    deleteShare,
    getShareUrl,
    copyShareUrl,
    setupWebSocketListeners,
  };
});
